import { createSlice } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";
import type { PlanState, PlanSubscription, SupplierSettings } from "App/store/slices/supplier.slice";

/**
 * Supplier plans (SUPPLIER_PLANS_PLAN.md §5): the plans on offer, choosing one,
 * card checkout, and the read-only ride settings.
 */

export interface PlanFeatureDef {
  key: string;
  type: "limit" | "bool";
  group: string;
  label: string;
  description: string;
  /** false = shown as "Coming soon". */
  live: boolean;
}

export interface Plan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  billing_period: "monthly" | "yearly" | "one_time";
  trial_days: number;
  trial_available?: boolean;
  features: Record<string, boolean | number | null>;
  default_settings: Partial<SupplierSettings>;
}

export const loadPlans = apiThunk<any, void>("plan/loadPlans", {
  server: "login",
  url: "suppliers/me/plans",
  method: "GET",
  errorMsg: true,
});

export const loadMyPlan = apiThunk<any, void>("plan/loadMyPlan", {
  server: "login",
  url: "suppliers/me/plan",
  method: "GET",
});

export const choosePlan = apiThunk<any, { planId: number }>("plan/choosePlan", {
  server: "login",
  url: "suppliers/me/plan",
  method: "POST",
  body: ({ planId }) => ({ plan_id: planId }),
  successMsg: true,
  errorMsg: true,
});

export const cancelPendingPlan = apiThunk<any, void>("plan/cancelPending", {
  server: "login",
  url: "suppliers/me/plan/pending",
  method: "DELETE",
  successMsg: true,
  errorMsg: true,
});

/** Hosted card checkout for a pending paid plan → { url }. */
export const startPlanCheckout = apiThunk<any, { subscriptionId: number }>("plan/checkout", {
  server: "payment",
  url: "synapto/supplier-plan/checkout",
  method: "POST",
  errorMsg: true,
});

export const loadMySettings = apiThunk<any, void>("plan/loadMySettings", {
  server: "login",
  url: "suppliers/me/settings",
  method: "GET",
  errorMsg: true,
});

interface PlanSliceState {
  plans: Plan[];
  features: PlanFeatureDef[];
  cardPayments: boolean;
  /** VAT % added to paid plan prices (0 = none). */
  vatRate: number;
  planState: PlanState | null;
  history: PlanSubscription[];
  settings: SupplierSettings | null;
  rulesActive: boolean;
  loading: boolean;
  choosing: boolean;
  checkingOut: boolean;
}

const initialState: PlanSliceState = {
  plans: [],
  features: [],
  cardPayments: false,
  vatRate: 0,
  planState: null,
  history: [],
  settings: null,
  rulesActive: false,
  loading: false,
  choosing: false,
  checkingOut: false,
};

const planSlice = createSlice({
  name: "plan",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadPlans.fulfilled, (state, action) => {
        const d = action.payload?.data || {};
        state.loading = false;
        state.plans = d.plans || [];
        state.features = d.features || [];
        state.cardPayments = Boolean(d.card_payments);
        state.vatRate = Number(d.vat_rate) || 0;
        state.planState = d.plan_state || null;
      })
      .addCase(loadPlans.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadMyPlan.fulfilled, (state, action) => {
        const d = action.payload?.data || {};
        state.planState = d.plan_state || null;
        state.history = d.history || [];
        if (d.features) state.features = d.features;
        state.cardPayments = Boolean(d.card_payments);
      })
      .addCase(choosePlan.pending, (state) => {
        state.choosing = true;
      })
      .addCase(choosePlan.fulfilled, (state) => {
        state.choosing = false;
      })
      .addCase(choosePlan.rejected, (state) => {
        state.choosing = false;
      })
      .addCase(startPlanCheckout.pending, (state) => {
        state.checkingOut = true;
      })
      .addCase(startPlanCheckout.fulfilled, (state) => {
        state.checkingOut = false;
      })
      .addCase(startPlanCheckout.rejected, (state) => {
        state.checkingOut = false;
      })
      .addCase(loadMySettings.fulfilled, (state, action) => {
        const d = action.payload?.data || {};
        state.settings = d.settings || null;
        state.rulesActive = Boolean(d.rules_active);
      });
  },
});

export default planSlice.reducer;
